import React, { memo } from 'react';
import { isEmpty } from 'lodash';
import { activeConsultationPackages } from '@fiverr-private/consultation';
import { I18n, translate } from '@fiverr-private/i18n-react';
import { Stack } from '@fiverr-private/layout_components';
import { logger } from '@fiverr-private/obs';
import {
    ContactButton,
    LocalTimeInfo,
    ConsultationButton,
    useIsSellerOnline,
    useLocalizationData,
    useConsultationData,
} from '@fiverr-private/seller_pages_toolbox';
import { Text } from '@fiverr-private/typography';
import { ErrorBoundary } from '@fiverr-private/ui_utils';
import { useContactMeProfessionBIExtensionData } from '../BIContext';
import { Heading } from './Heading';
import { buttonClass, consultationButtonClass } from './styles.ve.css';

const onRenderError = (originalError) => {
    const err = new Error('Failed while rendering the Sidebar Component');
    logger.error(err, {
        cause: originalError,
    });
};

export const RolePageContactCard = memo(
    ({ withAvatar = false, onContactOpen }: { withAvatar?: boolean; onContactOpen: () => void }) => {
        const localizationData = useLocalizationData();
        const consultationData = useConsultationData();
        const isOnline = useIsSellerOnline();
        const contactMeBIExtension = useContactMeProfessionBIExtensionData();
        const timeZone = localizationData?.timezone;
        const hasConsultation =
            !isEmpty(consultationData) && !!activeConsultationPackages(consultationData.pricing).length;

        return (
            <ErrorBoundary onError={onRenderError}>
                <Stack paddingX="8" paddingTop="8" paddingBottom="6" direction="column" gap="0">
                    <Heading withAvatar={withAvatar} />
                    <Stack gap="2" direction="column" paddingTop="8">
                        <ContactButton
                            className={buttonClass}
                            onContactOpen={onContactOpen}
                            biEnrichment={contactMeBIExtension}
                            title={translate('seller_page_perseus.professions_page.sidebar.contact') as string}
                        />
                        {hasConsultation && <ConsultationButton buttonClassName={consultationButtonClass} />}
                    </Stack>
                    <Stack justifyContent="center" paddingTop="3">
                        <Text size="b_sm" color="bodySecondary">
                            <Stack gap="0">
                                {isOnline ? (
                                    <I18n k="seller_page_perseus.professions_page.sidebar.online" />
                                ) : (
                                    <I18n k="seller_page_perseus.professions_page.sidebar.offline" />
                                )}
                                {timeZone && (
                                    <>
                                        &nbsp;•&nbsp;
                                        <LocalTimeInfo timeZone={timeZone} />
                                    </>
                                )}
                            </Stack>
                        </Text>
                    </Stack>
                </Stack>
            </ErrorBoundary>
        );
    }
);

RolePageContactCard.displayName = 'RolePageContactCard';
